import dayjs from 'dayjs';

export type CollectionStatus = 'tomorrow' | 'today' | 'missed-yesterday' | 'missed-earlier';

// An order line that has not had any courier scan yet has this tracking status
// (set when the order is placed; it changes on the first real tracking event).
const NO_SCANS_STATUS = 'New';

// How many days back we keep prompting to rebook an unscanned collection.
// Beyond this the order is likely lost/abandoned and better routed to claims.
const MISSED_WINDOW_DAYS = 7;

/**
 * Derives the collection-status message to show for a booked order line, based on
 * the customer's chosen collection date and whether any tracking scan has happened.
 *
 * Only true collection services qualify - `isDropOffService` must be explicitly
 * `false`. Drop-off services (and rows where the flag is unknown) return null, since
 * a `collectionDate` alone is not specific to collections.
 *
 * Returns null when there is nothing to show (drop-off/unknown service, no collection
 * date, a future date more than a day away, a missed date older than the prompt window,
 * or a past date that has since been scanned).
 */
export function getCollectionStatus(
  collectionDate: Date | undefined | null,
  trackingStatus: string | undefined,
  isDropOffService: boolean | undefined,
  now: Date = new Date()
): CollectionStatus | null {
  if (isDropOffService !== false) {
    return null;
  }

  if (!collectionDate) {
    return null;
  }

  const days = dayjs(collectionDate).startOf('day').diff(dayjs(now).startOf('day'), 'day');

  if (days === 1) {
    return 'tomorrow';
  }
  if (days === 0) {
    return 'today';
  }

  // Collection date is in the past with no scan yet: prompt to rebook, but only
  // within the recent window (yesterday gets its own friendlier wording).
  if (trackingStatus === NO_SCANS_STATUS && days <= -1 && days >= -MISSED_WINDOW_DAYS) {
    return days === -1 ? 'missed-yesterday' : 'missed-earlier';
  }

  return null;
}
